// Web push (Firebase Cloud Messaging) for the tech mobile/web app.
//
// Native shells keep using lib/pushNotifications.js (Capacitor). This
// module covers phones/browsers running the CRM as a PWA.
//
// Everything is a no-op until the Firebase web config + VAPID key are
// present in frontend/.env, so the UI degrades cleanly.
import { toast } from "sonner";
import API from "@/lib/api";

const FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

export const isWebPushConfigured = () =>
  Boolean(FIREBASE_CONFIG.projectId && FIREBASE_CONFIG.messagingSenderId && VAPID_KEY);

export const isWebPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const webPushPermission = () =>
  isWebPushSupported() ? Notification.permission : "unsupported";

let _messaging = null;

async function getMessagingInstance() {
  const { initializeApp, getApps } = await import("firebase/app");
  const { getMessaging, isSupported } = await import("firebase/messaging");
  if (!(await isSupported())) throw new Error("This browser can't receive web push");
  const app = getApps()[0] || initializeApp(FIREBASE_CONFIG);
  if (!_messaging) _messaging = getMessaging(app);
  return _messaging;
}

async function registerMessagingSW() {
  const qs = new URLSearchParams(
    Object.entries(FIREBASE_CONFIG).filter(([, v]) => Boolean(v))
  ).toString();
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${qs}`, {
    scope: SW_SCOPE,
  });
}

/** Ask for permission, mint an FCM token, hand it to the backend. */
export async function enableWebPush() {
  if (!isWebPushConfigured()) throw new Error("Firebase web push is not configured yet");
  if (!isWebPushSupported()) throw new Error("This device/browser doesn't support web push");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was denied");

  const messaging = await getMessagingInstance();
  const { getToken } = await import("firebase/messaging");
  const registration = await registerMessagingSW();
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error("Firebase returned no token — check the VAPID key");

  await API.post("/push/register", {
    token,
    platform: "web",
    device_label: navigator.userAgent || "web",
  });
  try {
    window.localStorage.setItem("__web_push_token", token);
  } catch {
    /* private mode */
  }
  return token;
}

/** Revoke the token locally and drop it from the backend. */
export async function disableWebPush() {
  let token = null;
  try {
    token = window.localStorage.getItem("__web_push_token");
  } catch {
    /* ignore */
  }
  if (isWebPushConfigured()) {
    try {
      const messaging = await getMessagingInstance();
      const { deleteToken } = await import("firebase/messaging");
      await deleteToken(messaging);
    } catch {
      /* token may already be gone */
    }
  }
  if (token) {
    try {
      await API.delete("/push/register", { data: { token } });
    } catch {
      /* best effort */
    }
    try {
      window.localStorage.removeItem("__web_push_token");
    } catch {
      /* ignore */
    }
  }
  return true;
}

/** Foreground toast — the OS won't draw a banner for the focused tab. */
export async function listenForegroundWebPush() {
  if (!isWebPushConfigured() || !isWebPushSupported()) return () => {};
  if (webPushPermission() !== "granted") return () => {};
  try {
    const messaging = await getMessagingInstance();
    const { onMessage } = await import("firebase/messaging");
    return onMessage(messaging, (payload) => {
      const n = payload?.notification || payload?.data || {};
      if (n.title) toast(n.title, { description: n.body || "" });
    });
  } catch {
    return () => {};
  }
}
